import React, { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { Button } from "../../../components/catalyst/button";
import {
  EyeIcon,
  PencilIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../config/firebaseConfig";
import { useToast } from "../../../util/ToastProvider";
import { doc, updateDoc, } from "firebase/firestore";
import { InboxArrowDownIcon } from "@heroicons/react/20/solid";

export default function EditText() {
  const location = useLocation();
  const nav = useNavigate();
  const { id } = useParams<{ id: string }>();
  const toaster = useToast();

  const initialText = (location.state as any)?.text || "";
  // const reportId = (location.state as any)?.reportId || id;
  let { text, reportId, type, annexureId } = (location.state as any) || {};

  if(!reportId){
    reportId = id
  }

  const [markdown, setMarkdown] = useState<string>(initialText);
  const [saving, setSaving] = useState(false);

  // 🧠 Save edited text → generate review version
  async function handleSave() {
    if (!reportId || !markdown.trim()) return;
    setSaving(true);
  
    try {
      const performReportReview = httpsCallable(functions, "performReportReview");
  
      if (type === "annexure") {
        await updateDoc(doc(db, "reports", reportId, "annexureResults", annexureId), {
          output: markdown,
          updatedAt: new Date(),
        });
      } else {
        await performReportReview({
          action: "GenerateReviewCycle",
          reportId,
          editedText: markdown,
        });
      }
  
      nav(`/admin/report/update/${reportId}`); 
      if (type === "annexure") {
        toaster.success("Annexure updated successfully.");
      } else {
        toaster.success("New version saved successfully.");
      }
    } catch (err: any) {
      console.error("❌ Save failed:", err.message || err);
      toaster.error("❌ Save failed:", err.message || err)
    } finally {
      setSaving(false);
    }
  }
  
  

  return (
    <div className="mx-8 my-6">
      {/* 🧩 Header */}
      <div className="flex justify-between items-center mb-5">
      <h2 className="text-[20px] font-semibold text-[rgba(49,44,133,1)]">
        {type === "annexure" ? "Edit annexure text" : "Edit report text"}
      </h2>

        <div className="flex gap-3">
          <Button color="soft" onClick={() => nav(-1)}>
            Cancel
          </Button>
          <Button color="brand" onClick={handleSave} disabled={saving}>
            <InboxArrowDownIcon className="w-4 h-4 mr-1" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* 🧭 Dual Panels Layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* ✏️ Left - Write Panel */}
        <div className="relative flex flex-col rounded-lg p-4">
          {/* Chip label */}
          <div className="inline-flex w-fit bg-white border border-[rgba(229,231,235,1)] rounded-full px-3 py-[2px] text-[13px] font-medium text-gray-600 items-center gap-1 shadow-sm mb-2">
  <PencilIcon className="w-4 h-4 text-gray-500" />
  Write
</div>

          <textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            className="w-full h-[500px] resize-none  outline-none text-[14px] text-[rgba(74,85,101,1)] placeholder-gray-400 mt-2 bg-[rgba(249,250,251,1)] border border-[rgba(229,231,235,1)]"
            placeholder="Start writing your markdown..."
          />
        </div>

        {/* 👁 Right - Preview Panel */}
        <div className="relative  rounded-lg p-4 overflow-y-auto h-[500px]">
          {/* Chip label */}
          <div className="inline-flex w-fit bg-white border border-[rgba(229,231,235,1)] rounded-full px-3 py-[2px] text-[13px] font-medium text-gray-600 items-center gap-1 shadow-sm mb-2">
  <EyeIcon className="w-4 h-4 text-gray-500" />
  Preview
</div>

          <div className="prose prose-sm max-w-none text-[14px] text-gray-700 mt-2 ">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize]}
            
          >
            {markdown || "_Nothing to preview yet..._"}
          </ReactMarkdown>
          </div>
          
        </div>
      </div>
    </div>
  );
}
